<script lang="ts">
	/**
	 * Every invoice and credit note the filter's window produced (Design §4.6, §13).
	 *
	 * **This is the store-wide document list, and it did not exist before.** An invoice was
	 * reachable only from the customer or the sale it belonged to, so "give me this quarter's
	 * invoices" meant visiting several hundred pages one at a time — which for anybody doing the
	 * books is not a workflow, it is a reason to keep a parallel spreadsheet, and that is how a
	 * store's books and its billing system come to disagree.
	 *
	 * It is read off the **same ledger** as every figure above it, so the documents for a window and
	 * the money for that window are the same set — the list is checkable against the totals rather
	 * than being a second answer to the same question.
	 *
	 * **Ours or theirs, never both.** A document the Store issued is a token-authenticated fetch of
	 * our own PDF; one the payment provider issued lives at the provider and is a real external
	 * link. The row renders whichever exists, and the archive can only contain the first — which is
	 * why the count is stated on the button rather than left to be discovered by opening the file.
	 */

	import { Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import {
		CreditNoteLink,
		EmptyState,
		ExternalLinkIcon,
		InfiniteLoadElement,
		Panel,
		RoutedLink,
		TableRowsPlaceholder
	} from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import { formatCalendarDate } from '../../../Utilities';
	import {
		fetchAdminInvoiceDocument,
		fetchReportDocumentArchive,
		listReportDocuments
	} from '../../../AppNetwork';
	import { toastError, toastInfo, toastSuccess } from '../../../stores/toast';
	import type { ReportDocument, ReportQuery } from '../../../AppTypes';

	export let filter: ReportQuery;

	const PAGE_SIZE = 50;

	let documents: ReportDocument[] = [];
	let total: number = 0;
	let ours: number = 0;
	let loading: boolean = true;
	let loadingMore: boolean = false;
	let error: string = '';
	let downloading: boolean = false;
	/** Which single document is opening, so one row's spinner does not blank its neighbours. */
	let opening: string = '';
	let sentinel: InfiniteLoadElement | undefined;

	/** Keyed on a string, for the reason the other panels' loads are. */
	$: queryKey = JSON.stringify(filter);
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			const page = await listReportDocuments(filter, PAGE_SIZE, 0);
			documents = page.items;
			total = page.total;
			ours = page.ours;
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the documents';
			documents = [];
			total = 0;
			ours = 0;
		} finally {
			loading = false;
			sentinel?.updateState();
		}
	}

	async function fnMore(): Promise<void> {
		if (loadingMore || loading || documents.length >= total) return;
		loadingMore = true;
		try {
			const page = await listReportDocuments(filter, PAGE_SIZE, documents.length);
			documents = [...documents, ...page.items];
			total = page.total;
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load more documents';
		} finally {
			loadingMore = false;
			sentinel?.updateState();
		}
	}

	/**
	 * One invoice, as a PDF.
	 *
	 * Only invoices reach here: a credit note goes through `CreditNoteLink`, which owns the same
	 * three rules for the other endpoint. Two components rather than a branch, because the endpoints
	 * are separate and a component that picked between them by inspecting a `kind` would 404 the day
	 * a row arrived carrying the wrong one — and a 404 there looks exactly like a missing document.
	 */
	async function fnOpen(document_: ReportDocument): Promise<void> {
		opening = document_.uuid;
		try {
			const blob = await fetchAdminInvoiceDocument(document_.uuid);
			window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
		} catch (caught) {
			toastError('The document could not be opened.', (caught as Error).message);
		} finally {
			opening = '';
		}
	}

	/**
	 * The bulk download.
	 *
	 * The counts come back in headers, and they are **reported rather than assumed**: the server
	 * caps how many PDFs it will render into one file, so a quarter with more documents than the cap
	 * produces a file that is genuinely short — and an archive quietly missing invoices is a set of
	 * books that does not reconcile, found out weeks later with nothing anywhere saying what went
	 * missing. The archive's own manifest says the same thing, for whoever opens it later.
	 */
	async function fnDownloadAll(): Promise<void> {
		downloading = true;
		try {
			const archive = await fetchReportDocumentArchive(filter);
			const url = URL.createObjectURL(archive.blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = archive.filename;
			anchor.click();
			// Revoked on a delay rather than immediately: Safari cancels a download whose blob has
			// already been released.
			setTimeout(() => URL.revokeObjectURL(url), 10000);

			if (archive.omitted > 0) {
				toastInfo(
					`${archive.included} document${archive.included === 1 ? '' : 's'} downloaded.`,
					`${archive.omitted} could not be included — the archive's manifest.csv says which and why.`
				);
			} else {
				toastSuccess(
					`${archive.included} document${archive.included === 1 ? '' : 's'} downloaded.`
				);
			}
		} catch (caught) {
			toastError('The documents could not be downloaded.', (caught as Error).message);
		} finally {
			downloading = false;
		}
	}

	function fnMoney(amount: number, currency: string): string {
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}

	/** How many of the listed documents are the provider's, which the archive cannot contain. */
	$: theirs = Math.max(0, total - ours);
</script>

<Panel title="Invoices and credit notes" faIcon="folder-open">
	<svelte:fragment slot="title">
		<span class="text-xs text-default">
			Every document this window produced, wherever it was issued.
		</span>
	</svelte:fragment>

	<svelte:fragment slot="titleRight">
		<Button
			size="xs"
			color="alternative"
			disabled={downloading || ours === 0}
			on:click={() => void fnDownloadAll()}
		>
			{#if downloading}
				Preparing…
			{:else}
				Download all ({ours})
			{/if}
		</Button>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The documents could not be loaded." description={error}>
			<Button color="alternative" size="sm" on:click={() => void fnLoad(queryKey)}>Try again</Button>
		</EmptyState>
	{:else if !loading && documents.length === 0}
		<EmptyState
			variant="empty"
			title="No documents in this range."
			description="Widen the range, or clear a filter that may be narrowing it to nothing."
		/>
	{:else}
		<p class="text-default text-sm mb-2">
			{total.toLocaleString()} document{total === 1 ? '' : 's'} in this window.
			<!--
				Stated on the face of the panel rather than discovered on opening the file. The store
				holds no copy of a document its payment provider issued, so those cannot be in the
				archive — and an archive short of half a quarter's invoices with nothing saying so is
				the failure this whole panel is written around.
			-->
			{#if theirs > 0}
				{theirs.toLocaleString()} of them were issued by the payment provider and are linked
				individually rather than included in the download.
			{/if}
		</p>

		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Document</TableHeadCell>
				<TableHeadCell>Issued</TableHeadCell>
				<TableHeadCell>Customer</TableHeadCell>
				<TableHeadCell>Product</TableHeadCell>
				<TableHeadCell class="text-right">Total</TableHeadCell>
				<TableHeadCell>Download</TableHeadCell>
			</TableHead>
			<TableBody>
				{#if loading}
					<TableRowsPlaceholder rows={6} columns={6} />
				{:else}
					{#each documents as document_ (`${document_.kind}:${document_.uuid}`)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div>{document_.reference}</div>
								<div class="text-xs text-default">
									{document_.kind === 'credit_note' ? 'Credit note' : 'Invoice'}
									{#if document_.sellerName !== null}· {document_.sellerName}{/if}
								</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{document_.issuedAt === null
									? '—'
									: formatCalendarDate(document_.issuedAt)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if document_.userUUID !== null}
									<!-- Routed, never a bare anchor: a full page load re-runs the AbstractID
									     handshake, and an admin working down this list would do it once a row. -->
									<RoutedLink
										to={`/Settings/Admin/Customers/${document_.userUUID}`}
										class="text-primary-500 hover:underline"
									>
										{document_.customerName ?? document_.customerEmail ?? 'Customer'}
									</RoutedLink>
								{:else}
									{document_.customerName ?? document_.customerEmail ?? '—'}
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{document_.productName ?? '—'}</TableBodyCell>
							<!-- `tabular-nums` for the reason every money column carries it: without it a
							     narrow column may break a total across two lines, and "−€22.6 / 1" still
							     looks like a number. -->
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(document_.total, document_.currency)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if document_.kind === 'credit_note'}
									<CreditNoteLink
										uuid={document_.uuid}
										documentNumber={document_.reference}
										label="PDF"
										admin
									/>
								{:else if document_.ours}
									<!-- Ours is a fetch, not a navigation: the endpoint is token-authenticated
									     and the token travels in a header. -->
									<button
										type="button"
										class="text-primary-500 hover:underline disabled:opacity-50"
										title={`Invoice ${document_.reference}`}
										disabled={opening === document_.uuid}
										on:click={() => void fnOpen(document_)}
									>
										{opening === document_.uuid ? 'Opening…' : 'PDF'}
									</button>
								{:else if document_.hostedURL !== null || document_.pdfURL !== null}
									<!-- Theirs leaves this origin, so it is a real external link and gets the
									     icon that says so. -->
									<a
										class="text-primary-500 hover:underline"
										href={document_.pdfURL ?? document_.hostedURL}
										target="_blank"
										rel="noopener noreferrer"
									>
										At provider<ExternalLinkIcon class="ml-1 inline h-3 w-3 align-baseline" />
									</a>
								{:else}
									<!-- Not an error: a draft invoice has no document yet, and will. -->
									<span class="text-default text-xs">Not issued yet</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				{/if}
			</TableBody>
		</Table>

		{#if !loading && documents.length < total}
			<InfiniteLoadElement
				bind:this={sentinel}
				class="w-full"
				on:becomeVisible={() => void fnMore()}
			>
				{#if loadingMore}
					<EmptyState variant="loading" compact title="Loading more…" />
				{/if}
			</InfiniteLoadElement>
		{/if}
	{/if}
</Panel>
